<script setup lang="ts">
import { ref } from "vue";
import { Option } from "@/types";
import { useAutocompleteEquipmentId } from "@/api/mutations";

interface Props {
  customerId: string;
  labelClasses: string;
}

const autocompleteEquipmentIdMutation = useAutocompleteEquipmentId();

const props = defineProps<Props>();
const optionList = ref<Option[]>([]);

async function searchAssets({ search }: { search: string }): Promise<Option[]> {
  if (!search || search.length < 3) {
    return optionList.value;
  }

  const responseData = await autocompleteEquipmentIdMutation.mutateAsync({
    customerId: props.customerId,
    requestData: {
      search_term: search,
      page: 1,
      page_size: 100,
    },
  });

  if (!responseData.data.length) {
    return optionList.value;
  }

  const out: Option[] = [];
  for (const i of responseData.data) {
    if (!optionList.value.find((opt) => opt.value === i.equipment_id)) {
      out.push({
        label: i.equipment_id,
        value: i.equipment_id,
      });
    }
  }

  return optionList.value.concat(out);
}

async function optionLoader(value: string, cached?: Option): Promise<Option> {
  if (cached) {
    if (!optionList.value.find((opt) => opt.value === cached.value)) {
      optionList.value.push(cached);
    }
    return cached;
  }

  const responseData = await autocompleteEquipmentIdMutation.mutateAsync({
    customerId: props.customerId,
    requestData: {
      search_term: value,
      page: 1,
      page_size: 100,
    },
  });

  if (!responseData.data.length) {
    return { label: "No matching equipment found.", value: "" };
  }

  const out: Option[] = [];
  for (const i of responseData.data) {
    out.push({
      label: i.equipment_id,
      value: i.equipment_id,
    });
  }

  optionList.value.concat(out);
  return out[0];
}
</script>

<template>
  <FormKit
    type="autocomplete"
    label="Asset ID"
    name="equipment_id"
    :classes="{
      label: labelClasses + ' grow',
      outer: '!grow-0 content-center !mb-0',
      wrapper: 'flex gap-2 grow',
      inner: 'w-40',
      selectionWrapper: 'bg-card border-none rounded-md m-px justify-between',
      selection: 'font-semibold',
    }"
    :close-on-select="true"
    selection-appearance="option"
    loader-icon="loader"
    :options="searchAssets"
    :option-loader="optionLoader"
    empty-message="No equipment found."
    placeholder="- -"
  >
    <template #loadMore></template>
  </FormKit>
</template>
